import { Injectable, Type, ComponentFactoryResolver, Injector, OnDestroy } from '@angular/core';
import * as L from 'leaflet';
import { MapStoreService } from '../services/map-store.service';
import { LampPopupComponent } from '@app/energy-map/map-popup/lamp-popup/lamp-popup.component';
import { BehaviorSubject, of, fromEvent, Subject, Observable } from 'rxjs';
import { chain, minBy, isNil, uniq, every, delay } from 'lodash';
import { tap, map, filter, debounceTime, takeUntil } from 'rxjs/operators';
import { MapService } from '../services/map.service';
import { LampsSelectedService } from '../services/lamps-selected.service';
import { MapPopupComponent } from '../map-popup/map-popup.component';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { Layer } from 'leaflet';
import { EPMarker } from '@app/shared/models/energiekarte/epmarker';
import { Luminaire } from '@app/shared/models/energiekarte/luminaire';

@Injectable({
  providedIn: 'root',
})
export class StreetlightLayerService implements OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  private hoverChange = new BehaviorSubject<any>(null);
  private isManualSelectModeOn = false;
  private delayedTimeouts: any[] = [];
  private mapSelect: any;

  constructor(
    private mapStoreService: MapStoreService,
    private mapService: MapService,
    private lampsSelectedService: LampsSelectedService,
    private resolver: ComponentFactoryResolver,
    private injector: Injector,
    private overlayLoader: OverlayLoaderService
  ) {
    this.mapService.manualSelectModeChange.pipe(takeUntil(this.unsubscribe$)).subscribe((value: boolean) => {
      if (value) {
        this.mapSelect.selectArea.enable();
      } else {
        this.mapSelect.selectArea.disable();
      }
      this.isManualSelectModeOn = value;
    });

    this.hoverChange.pipe(debounceTime(500), takeUntil(this.unsubscribe$)).subscribe((marker: any) => {
      if (marker?.shown) {
        this.markerHoverEvent(marker.target);
      }
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  selectArea = (e: any) => {
    if (!this.isManualSelectModeOn) {
      return;
    }

    try {
      this.overlayLoader.isLoading = true;

      const boundedMarkers: any[] = [];
      const unselectedMarkers: any[] = [];

      (this.mapStoreService.map as L.Map).eachLayer((marker: Layer) => {
        if (marker instanceof L.MarkerCluster) {
          if (e.bounds.contains(marker.getLatLng())) {
            const markers = marker.getAllChildMarkers();
            const option = markers[0].options as any;
            if (option.type === 'lamp') {
              marker.getAllChildMarkers().forEach((m) => {
                const opt = m.options as any;
                const allExempted = every(opt.data.lamps, (l) => this.lampsSelectedService.isExcludedFromPlanning(l));
                if (!allExempted) {
                  boundedMarkers.push(m);
                }
              });
            }
          }
        } else if (marker instanceof L.Marker) {
          const option = marker.options as any;
          if (e.bounds.contains(marker.getLatLng()) && option.type === 'lamp') {
            const allExempted = every(option.data.lamps, (l) => this.lampsSelectedService.isExcludedFromPlanning(l));

            if (!allExempted) {
              boundedMarkers.push(marker);
            }
          }
        }
      });

      unselectedMarkers.push(...boundedMarkers.filter((element: any) => element.options.isSelected === false || isNil(element.options.isSelected)));

      const select = unselectedMarkers.length > 0;
      const markersToUpdate = select ? unselectedMarkers : boundedMarkers;

      const lamps: any[] = [];

      let i = 0;
      const length = markersToUpdate.length;

      if (length === 0) {
        delay(() => {
          this.overlayLoader.isLoading = false;
        }, 1000);
        return;
      }

      const process = () => {
        for (; i < length; i++) {
          if (!this.isManualSelectModeOn) {
            delay(() => {
              this.overlayLoader.isLoading = false;
            }, 1000);
            return;
          }

          let markerIsHighlighted = false;

          lamps.push(markersToUpdate[i].options.data);

          if (select) {
            markerIsHighlighted = markersToUpdate[i].options.isHighlighted;
          }

          markersToUpdate[i].options.data.isRecommended = false;
          markersToUpdate[i].options.isHighlighted = markerIsHighlighted;
          markersToUpdate[i].options.isSelected = select;
          if (i === length - 1) {
            delay(() => {
              this.overlayLoader.isLoading = false;
            }, 1000);
            this.mapSelect.selectArea.enable();
            if (select) {
              lamps.forEach((streetLight) => {
                this.lampsSelectedService.addLamps(streetLight.lamps);
              });
            } else {
              lamps.forEach((streetLight) => {
                this.lampsSelectedService.removeLamps(streetLight.lamps);
              });
            }

            this.mapService.updateLampMarker(markersToUpdate, {});
            this.mapStoreService.streetLightCluster.refreshClusters();

            return;
          } else if (i !== 0 && i + 1 < length && i % 5 === 0) {
            this.mapSelect.selectArea.disable();
            setTimeout(process, 0);
            i += 1;
            return;
          }
        }
      };

      process();
    } catch (err) {
      delay(() => {
        this.overlayLoader.isLoading = false;
      }, 1000);
    }
  };

  public initializeAreaSelect() {
    this.mapSelect = this.mapStoreService.map as L.Map as any;
    this.mapSelect.selectArea.setControlKey(true);

    this.mapSelect.on('areaselectstart', () => {
      (this.mapStoreService.map as L.Map).fireEvent('click');
      (this.mapStoreService.map as L.Map).closePopup();
    });

    this.mapSelect.on('areaselected', this.selectArea);
  }

  public initializeLamps(lamps: Luminaire[]): Observable<boolean> {
    if (lamps.length === 0) {
      return of(true);
    }

    const cluster = L.markerClusterGroup({
      iconCreateFunction: this.getLampClusterIcon.bind(this),
    });

    const heatmap = L.layerGroup();

    const streetLights = chain(lamps)
      .groupBy((lamp) => lamp.equipment)
      .map((value, key) => ({
        equipment: key,
        coordinates: {
          latitude: value[0].address.easting,
          longitude: value[0].address.northing,
        },
        lightLumenPerWatt: (minBy(value, (lamp: Luminaire) => lamp.lightLumenPerWatt) ?? { lightLumenPerWatt: 0 }).lightLumenPerWatt,
        hasMultipleLamps: value.length > 1,
        address: value[0].address,
        mastHeight: value[0].mastHeight,
        bstNumber: value[0].bstNumber,
        lamps: value,
      }))
      .value();

    return this.processStreetLights(streetLights, cluster, heatmap).pipe(
      tap(() => {
        this.mapStoreService.updateStreetLightCluster(cluster);
        this.mapStoreService.updateStreetLightHeatmap(heatmap);
      }),
      map(() => true)
    );
  }

  getLampClusterIcon(markers: any) {
    const childMarkers = markers.getAllChildMarkers();

    const lampColorCodes = childMarkers.map((a: any) => a.options.data.lamps[0].colorCode).filter((a: string) => !isNil(a) && a.length > 0);
    const uniqueColorCodes: any[] = uniq(lampColorCodes);
    let innerDiv = '';
    if (uniqueColorCodes.length === 3) {
      const colorCode1 = uniqueColorCodes[0];
      const colorCode2 = uniqueColorCodes[1];
      const colorCode3 = uniqueColorCodes[2];

      const colorCode1Highligted =
        childMarkers.filter((x: any) => x.options.data.lamps[0].colorCode === colorCode1 && x.options.isHighlighted === true).length > 0;
      const colorCode2Highligted =
        childMarkers.filter((x: any) => x.options.data.lamps[0].colorCode === colorCode2 && x.options.isHighlighted === true).length > 0;
      const colorCode3Highligted =
        childMarkers.filter((x: any) => x.options.data.lamps[0].colorCode === colorCode3 && x.options.isHighlighted === true).length > 0;

      const colorCode1Selected = childMarkers.filter((x: any) => x.options.data.lamps[0].colorCode === colorCode1 && x.options.isSelected === true).length > 0;
      const colorCode2Selected = childMarkers.filter((x: any) => x.options.data.lamps[0].colorCode === colorCode2 && x.options.isSelected === true).length > 0;
      const colorCode3Selected = childMarkers.filter((x: any) => x.options.data.lamps[0].colorCode === colorCode3 && x.options.isSelected === true).length > 0;

      innerDiv = `<div class="cluster-container">
          <div class="lamp-circle -cluster
            ${colorCode1Selected ? ' -selected ' : ''}
            ${colorCode1Highligted ? ' -highlighted ' : ''}
            "
            style="background-color: #${colorCode1};
            ${colorCode1Selected ? this.mapService.getLampShadowStyle(colorCode1, 0.3) : ''}
            ${colorCode1Highligted ? this.mapService.getLampShadowStyle(colorCode1, 1) : ''}
            "
          >
          ${lampColorCodes.filter((a: any) => a === colorCode1).length}
          </div>

          <div class="lamp-circle -cluster -right
            ${colorCode2Selected ? ' -selected ' : ''}
            ${colorCode2Highligted ? ' -highlighted ' : ''}
            "
            style="background-color: #${colorCode2};
            ${colorCode2Selected ? this.mapService.getLampShadowStyle(colorCode2, 0.3) : ''}
            ${colorCode2Highligted ? this.mapService.getLampShadowStyle(colorCode2, 1) : ''}
            "
          >
          ${lampColorCodes.filter((a: any) => a === colorCode2).length}
          </div>

          <div class="lamp-circle -cluster -bottom
            ${colorCode3Selected ? ' -selected ' : ''}
            ${colorCode3Highligted ? ' -highlighted ' : ''}
            "
            style="background-color: #${colorCode3};
            ${colorCode3Selected ? this.mapService.getLampShadowStyle(colorCode2, 0.3) : ''}
            ${colorCode3Highligted ? this.mapService.getLampShadowStyle(colorCode2, 1) : ''}
            "
          >
          ${lampColorCodes.filter((a: any) => a === colorCode3).length}
          </div></div>
          `;
    }

    if (uniqueColorCodes.length === 2) {
      const colorCode1 = uniqueColorCodes[0];
      const colorCode2 = uniqueColorCodes[1];

      const colorCode1Highligted =
        childMarkers.filter((x: any) => x.options.data.lamps[0].colorCode === colorCode1 && x.options.isHighlighted === true).length > 0;
      const colorCode2Highligted =
        childMarkers.filter((x: any) => x.options.data.lamps[0].colorCode === colorCode2 && x.options.isHighlighted === true).length > 0;

      const colorCode1Selected = childMarkers.filter((x: any) => x.options.data.lamps[0].colorCode === colorCode1 && x.options.isSelected === true).length > 0;
      const colorCode2Selected = childMarkers.filter((x: any) => x.options.data.lamps[0].colorCode === colorCode2 && x.options.isSelected === true).length > 0;

      innerDiv = `<div class="cluster-container">
          <div class="lamp-circle -cluster
            ${colorCode1Selected ? ' -selected ' : ''}
            ${colorCode1Highligted ? ' -highlighted ' : ''}
            "
            style="background-color: #${colorCode1};
            ${colorCode1Selected ? this.mapService.getLampShadowStyle(colorCode1, 0.3) : ''}
            ${colorCode1Highligted ? this.mapService.getLampShadowStyle(colorCode1, 1) : ''}
            "
          >
          ${lampColorCodes.filter((a: any) => a === colorCode1).length}
          </div>

          <div class="lamp-circle -cluster -right
            ${colorCode2Selected ? ' -selected ' : ''}
            ${colorCode2Highligted ? ' -highlighted ' : ''}
            "
            style="background-color: #${colorCode2};
            ${colorCode2Selected ? this.mapService.getLampShadowStyle(colorCode2, 0.3) : ''}
            ${colorCode2Highligted ? this.mapService.getLampShadowStyle(colorCode2, 1) : ''}
            "
          >
          ${lampColorCodes.filter((a: any) => a === colorCode2).length}
          </div></div>
          `;
    }

    if (uniqueColorCodes.length === 1) {
      const colorCode1 = uniqueColorCodes[0];

      const colorCode1Highligted =
        childMarkers.filter((x: any) => x.options.data.lamps[0].colorCode === colorCode1 && x.options.isHighlighted === true).length > 0;

      const colorCode1Selected = childMarkers.filter((x: any) => x.options.data.lamps[0].colorCode === colorCode1 && x.options.isSelected === true).length > 0;

      innerDiv = `<div class="cluster-container">
          <div class="lamp-circle -cluster
          ${colorCode1Selected ? ' -selected ' : ''}
          ${colorCode1Highligted ? ' -highlighted ' : ''}
          "
          style="background-color: #${colorCode1};
          ${colorCode1Selected ? this.mapService.getLampShadowStyle(colorCode1, 0.3) : ''}
          ${colorCode1Highligted ? this.mapService.getLampShadowStyle(colorCode1, 1) : ''}
          "
        >
        ${lampColorCodes.filter((a: any) => a === colorCode1).length}
        </div></div>
          `;
    }

    return L.divIcon({
      html: innerDiv,
      iconSize: L.point(20, 20),
    });
  }

  processStreetLights = (streetLights: any, cluster: any, heatmap: any) =>
    of(streetLights).pipe(map((lights) => lights.map((streetLight: any) => this.processStreetLightMarker(streetLight, cluster, heatmap))));

  processStreetLightMarker = (streetLight: any, cluster: any, heatmap: any) => {
    const marker = this.createStreetLightMarker(streetLight);
    this.streetLightMarkerSeletected(marker).pipe(takeUntil(this.unsubscribe$)).subscribe();
    this.showStreetLightPopupOnMouseOver(marker).pipe(takeUntil(this.unsubscribe$)).subscribe();
    this.removeStreetLightPopupOnMouseOut(marker).pipe(takeUntil(this.unsubscribe$)).subscribe();
    this.removeStreetLightDelayedTimeuotOnMouseOut(marker);
    this.refreshStreetLightPopupOnPopOpen(marker).pipe(takeUntil(this.unsubscribe$)).subscribe();

    this.addStreetLightMarkerToCluster(marker, cluster);
    this.addStreetLightMarkerToCluster(marker, heatmap);
    this.addStreetLightMarkerToMapStoreService(marker);
  };

  createStreetLightMarker = (streetLight: any) =>
    new EPMarker([streetLight.coordinates.latitude, streetLight.coordinates.longitude], {
      icon: this.createStreetLightIcon(streetLight),
      id: streetLight.equipment,
      equipment: streetLight.equipment,
      data: streetLight,
      type: 'lamp',
      component: null,
      isSelected: false,
      isHighlighted: false,
      hasMultipleLamps: streetLight.hasMultipleLamps,
    });

  createStreetLightIcon = (streetLight: any) =>
    streetLight.hasMultipleLamps
      ? this.mapService.getMultipleLampIcon(streetLight.lamps[0].colorCode, streetLight.lamps.length, { isSelected: false, isHighlighted: false })
      : this.mapService.getSingleLampIcon(streetLight.lamps[0].colorCode, {
          isSelected: false,
          isHighlighted: false,
        });

  streetLightMarkerSeletected = (marker: any) =>
    this.streetLightMarkerClickEvent(marker).pipe(
      filter(({ target }) => !target.options.hasMultipleLamps),
      tap(this.markerClickEvent)
    );

  markerClickEvent = ({ target }: any) => {
    if (this.isManualSelectModeOn && !this.lampsSelectedService.isExcludedFromPlanning(target.options.data.lamps[0])) {
      this.mapService.lampClicked(true);
      this.clickStreetLight(target);
    }
  };

  clickStreetLight(marker: any) {
    marker.options.component.changeDetectorRef.detectChanges();

    let markerIsHighlighted = !isNil(marker.options.isHighlighted) ? marker.options.isHighlighted : false;

    marker.options.data.isRecommended = false;
    const isMultipleLuminaire = marker.options.data.hasMultipleLamps;

    if (isMultipleLuminaire) {
      marker.openPopup();
    } else {
      if (marker.options.isSelected === true) {
        marker.options.data.lamps.forEach((lamp: any) => {
          this.lampsSelectedService.remove(lamp);
          markerIsHighlighted = false;
        });
      } else {
        marker.options.data.lamps.forEach((lamp: any) => {
          this.lampsSelectedService.addLamp(lamp);
          markerIsHighlighted = marker.options.isHighlighted;
        });
      }
      this.mapService.setClassLamp(marker, {
        isSelected: marker.options.isSelected,
        isHighlighted: markerIsHighlighted,
      });
    }
  }

  streetLightMarkerClickEvent = (marker: any) =>
    fromEvent(marker, 'click').pipe(
      tap(({ target }: any) => {
        if (this.isManualSelectModeOn && !target.options.hasMultipleLamps && this.lampsSelectedService.isExcludedFromPlanning(target.options.data.lamps[0])) {
          return;
        }

        if (target.options.component === null) {
          target = this.initializeStreetLightPopup(target);
          target.options.component.instance.isManualSelect = this.isManualSelectModeOn;
          target.options.component.instance.isClicked = true;
        }

        target.options.component.instance.isManualSelect = this.isManualSelectModeOn;
        target.options.component.instance.isClicked = true;
      })
    );

  initializeStreetLightPopup = (marker: any) => {
    const component = this.initializeLampPopup(LampPopupComponent, marker.options.data);
    marker.options.component = component;
    this.openLampDetail(marker);
    this.updateStreetLightPopup(marker);
    this.closeLampDetail(marker);
    this.updateStreetLightCheckBox(marker);
    const popUp = L.popup({
      closeButton: false,
    }).setContent(marker.options.component.location.nativeElement);
    marker.bindPopup(popUp);

    return marker;
  };

  openLampDetail = (marker: any) =>
    marker.options.component.instance.openLampDetail.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      marker.options.component.changeDetectorRef.detectChanges();
      marker.options.icon.options.popupAnchor = [26, -54];
      marker.openPopup();
    });

  closeLampDetail = (marker: any) =>
    marker.options.component.instance.closeLampDetail.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      marker.options.component.changeDetectorRef.detectChanges();
      marker.options.icon.options.popupAnchor = [-100, -330];
      marker.openPopup();
    });

  updateStreetLightCheckBox = (marker: any) =>
    marker.options.component.instance.updateCheckbox
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => marker.options.component.changeDetectorRef.detectChanges());

  updateStreetLightPopup = (marker: any) =>
    marker.options.component.instance.updatePopup
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => marker.options.component.changeDetectorRef.detectChanges());

  initializeLampPopup(popupComponent: Type<MapPopupComponent>, lamps: any) {
    const component = this.resolver.resolveComponentFactory(popupComponent).create(this.injector);

    component.instance.data = lamps;
    component.instance.closeEvent.pipe(takeUntil(this.unsubscribe$)).subscribe(() => this.closePopup());
    component.changeDetectorRef.detectChanges();

    return component;
  }

  closePopup = () => {
    (this.mapStoreService.map as L.Map).closePopup();
  };

  showStreetLightPopupOnMouseOver = (marker: any) =>
    this.streetLightMarkerMouseOverEvent(marker).pipe(
      tap(({ target }: any) => {
        if (!(this.mapStoreService.map as L.Map).hasLayer(target.getPopup()) && this.isManualSelectModeOn) {
          this.hoverChange.next(Object.assign({}, { target, shown: true } as any));
        }
      })
    );

  streetLightMarkerMouseOverEvent = (marker: any) =>
    fromEvent(marker, 'mouseover').pipe(
      tap(({ target }: any) => {
        if (target.options.component === null) {
          target = this.initializeStreetLightPopup(target);
          target.options.component.instance.isManualSelect = this.isManualSelectModeOn;
          target.options.component.instance.isClicked = false;
        }
      })
    );

  removeStreetLightPopupOnMouseOut = (marker: any) =>
    this.streetLightMarkerMouseOutEvent(marker).pipe(tap(({ target }: any) => this.hoverChange.next(Object.assign({}, { target, shown: false } as any))));

  removeStreetLightDelayedTimeuotOnMouseOut = (marker: any) =>
    this.streetLightMarkerMouseOutEvent(marker).pipe(
      tap(({ target }: any) => {
        this.delayedTimeouts.forEach((u) => clearTimeout(u));
        this.delayedTimeouts = [];
        this.hoverChange.next(Object.assign({}, { target, shown: false } as any));
      })
    );

  streetLightMarkerMouseOutEvent = (marker: any) => fromEvent(marker, 'mouseout');

  refreshStreetLightPopupOnPopOpen = (marker: any) =>
    this.streetLightMarkerPopupOpenEvent(marker).pipe(tap(({ target }: any) => target.options.component.changeDetectorRef.detectChanges()));

  addStreetLightMarkerToCluster = (streetLight: any, cluster: any) => streetLight.addTo(cluster);

  addStreetLightMarkerToMapStoreService = (streetLight: any) => this.mapStoreService.addLampMarker(streetLight);

  streetLightMarkerPopupOpenEvent = (marker: any) => fromEvent(marker, 'popupopen');

  markerHoverEvent = (target: any) => {
    if (this.isManualSelectModeOn) {
      this.mapService.lampClicked(false);
      target.openPopup();
    }
  };
}
